"use client"

import { useState, useMemo, useEffect } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Search, Play, ArrowRight, Filter, Zap } from "lucide-react"
import Header from "@/components/Header"
import Footer from "@/components/Footer"
import { SearchInput } from "../components/SearchInput.tsx"
import { EmptyState } from "../components/EmptyState.tsx"
import { GalleryGridSkeleton, EmptySearchResults } from "@/components/common"
// Not used:
// import { GalleryHero } from "../components/GalleryHero"
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet"

import { workflowGalleryService } from "@/lib/api/services";
import { useApi } from "@/hooks/useApi";
import type { WorkflowData } from '@/lib/types';
import { WorkflowCard } from "./components/WorkflowCard.tsx";
import { WorkflowPreviewDialog } from "./components/WorkflowPreviewDialog.tsx";

export default function WorkflowGallery() {
  const [searchQuery, setSearchQuery] = useState("")
  const [selectedCategory, setSelectedCategory] = useState("All Workflows")
  const [previewWorkflow, setPreviewWorkflow] = useState<any>(null)

  // Fetch workflows from API
  const workflowsResult = useApi(workflowGalleryService.list, [], { showErrorToast: true });

  // Get data from API
  const allWorkflows = workflowsResult.data?.items || [];

  // Client-side filtering
  const filteredWorkflows = allWorkflows.filter((workflow: any) => {
    const matchesSearch = !searchQuery ||
      workflow.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      workflow.description?.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesCategory = selectedCategory === "All Workflows" ||
      workflow.category === selectedCategory;

    return matchesSearch && matchesCategory;
  });

  const workflows = filteredWorkflows;
  const isLoading = workflowsResult.loading;
  
  // Get categories from API
  const categoriesResult = useApi(workflowGalleryService.getCategories, [], { showErrorToast: false });
  const categories = ['All Workflows', ...(categoriesResult.data || [])];

  const handlePreview = (workflow: any) => {
    setPreviewWorkflow(workflow)
  }

  const handleClone = async (workflow: any) => {
    throw new Error('Cloning workflows requires authentication. Please log in to the dashboard.');
  }

  const handleRunDemo = (workflow: WorkflowData) => {
    // In a real app, this would run a demo of the workflow
    alert(`Running demo for "${workflow.title}"...`)
    // Here you would typically:
    // 1. Create a temporary instance
    // 2. Execute with sample data
    // 3. Show results in a modal or new page
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 dark:from-slate-900 dark:to-slate-800">
      <Header currentPage="workflows" variant="gallery" />

      {/* Hero Section */}
      <header className="workflow-hero text-left border-b border-slate-200 bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 text-white relative overflow-hidden">
        <div>
        <div className="p-8 md:p-20 max-w-screen-2xl mx-auto">
          <h1 className="text-4xl md:text-6xl font-extrabold text-black mb-4">
            Workflow Gallery
          </h1>
          <p className="max-w-3xl pt-2 text-lg md:text-xl text-blue-900 mb-8">
            Discover, preview, and clone powerful AI workflows built by our community. 
            From simple automations to complex data pipelines.
          </p>
          <div className="flex flex-wrap gap-4">
            <Button size="lg" className="bg-blue-600 text-white font-semibold py-3 px-6 rounded-lg shadow-md hover:bg-blue-700 transition">
              <Play className="mr-2 h-5 w-5" />
              Browse Workflows
            </Button>
            <Button size="lg" variant="outline" className="bg-white text-blue-600 font-semibold py-3 px-6 rounded-lg border hover:bg-blue-50 transition">
              Create Your Own <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </div>
        </div>
      </header>

      {/* Search and Filters */}
      <section className="container mx-auto px-4 py-12 mb-8">
        <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
          <SearchInput
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder="Search workflows, authors, or tags..."
            className="flex-1 max-w-md"
          />

          {/* Desktop Category Buttons */}
          <div className="hidden md:flex flex-wrap gap-2">
            {categories.map((category) => (
              <Button
                key={category}
                variant={category === selectedCategory ? "default" : "outline"}
                size="sm"
                className={category === selectedCategory ? "bg-blue-600 hover:bg-blue-700" : ""}
                onClick={() => setSelectedCategory(category)}
              >
                {category}
              </Button>
            ))}
          </div>

          {/* Mobile Filter Button and Sheet */}
          <div className="md:hidden flex justify-end w-full">
            <Sheet>
              <SheetTrigger asChild>
                <Button variant="outline" className="flex items-center gap-2 w-full">
                  <Filter className="h-4 w-4" />
                  Filters
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-80">
                <SheetHeader>
                  <SheetTitle>Filters</SheetTitle>
                </SheetHeader>
                <div className="space-y-6 overflow-y-auto h-full py-4">
                  {/* Search Input in Sheet */}
                  <SearchInput
                    value={searchQuery}
                    onChange={setSearchQuery}
                    placeholder="Search workflows, authors, or tags..."
                  />
                  {/* Category Buttons in Sheet */}
                  <div className="flex flex-col gap-2">
                    {categories.map((category) => (
                      <Button
                        key={category}
                        variant={category === selectedCategory ? "default" : "outline"}
                        className={category === selectedCategory ? "bg-blue-600 hover:bg-blue-700" : ""}
                        onClick={() => setSelectedCategory(category)}
                      >
                        {category}
                      </Button>
                    ))}
                  </div>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setSearchQuery("")
                      setSelectedCategory("All Workflows")
                    }}
                  >
                    Clear filters
                  </Button>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>

        {searchQuery && (
          <div className="mt-4 text-sm text-slate-600 dark:text-slate-400">
            Found {workflows.length} workflow{workflows.length !== 1 ? "s" : ""} matching "{searchQuery}"
            
          </div>
        )}
      </section>

      {/* Workflow Grid */}
      <section className="container mx-auto px-4 pb-16">
        {isLoading ? (
          <GalleryGridSkeleton count={6} type="workflow" />
        ) : workflows.length === 0 ? (
          searchQuery ? (
            <EmptySearchResults 
              searchTerm={searchQuery}
              onAction={() => {
                setSearchQuery("");
                setSelectedCategory("All Workflows");
              }}
              actionLabel="Clear Search"
              className="max-w-md mx-auto"
            />
          ) : (
            <EmptyState 
              icon={Zap}
              title="No workflows available"
              description="Browse our workflow gallery to discover community-created automations."
            />
          )
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {workflows.map((workflow: WorkflowData) => (
              <WorkflowCard
                key={workflow.id}
                workflow={workflow}
                onPreview={handlePreview}
                onClone={handleClone}
              />
            ))}
          </div>
        )}
      </section>

      <WorkflowPreviewDialog
        previewWorkflow={previewWorkflow}
        setPreviewWorkflow={setPreviewWorkflow}
        handleClone={handleClone}
        handleRunDemo={handleRunDemo}
      />

      <Footer />
    </div>
  )
}
